import { useCallback, useMemo, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { Group, Panel, Separator } from 'react-resizable-panels';
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import {
  DsButton,
  DsSearchInput,
  DsSegmented,
  type DsSegmentedOption,
} from '@/modules/internal-v2/ds';
import { useContactLists } from '@/api/contact-list/contact-list.hooks';
import type {
  ContactList,
  ContactListKind,
} from '@/api/contact-list/contact-list.types';
import { DsTable } from '../shared/ds-table.component';
import { ContactListModal } from './components/contact-list-modal.component';

const KIND_LABELS: Record<ContactListKind, string> = {
  customer: 'Kunden',
  supplier: 'Lieferanten',
};

type KindFilter = 'all' | ContactListKind;

const KIND_FILTER_OPTIONS: DsSegmentedOption<KindFilter>[] = [
  { value: 'all', label: 'Alle' },
  { value: 'customer', label: 'Kunden' },
  { value: 'supplier', label: 'Lieferanten' },
];

/** Context provided by {@link VerteilerPage} to its detail routes. */
export interface VerteilerListContext {
  lists: ContactList[];
  listsPending: boolean;
  listsFetching: boolean;
}

const fmtDate = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getFullYear()).slice(2)}`;
};

const col = createColumnHelper<ContactList>();

const masterColumns = [
  col.accessor('name', {
    header: 'Verteiler',
    meta: { flex: true },
    sortingFn: 'text',
    cell: (info) => (
      <span style={{ fontWeight: 500, color: 'var(--ink)' }}>
        {info.getValue()}
      </span>
    ),
  }),
  col.accessor('kind', {
    header: 'Typ',
    size: 92,
    enableSorting: false,
    cell: (info) => (
      <span className="vt-kind-chip" data-kind={info.getValue()}>
        {KIND_LABELS[info.getValue()]}
      </span>
    ),
  }),
  col.accessor((row) => row.members.length, {
    id: 'memberCount',
    header: 'Kontakte',
    size: 72,
    meta: { numeric: true },
    cell: (info) => (
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          color: info.getValue() > 0 ? 'var(--ink-2)' : 'var(--ink-4)',
        }}
      >
        {info.getValue() || '—'}
      </span>
    ),
  }),
  col.accessor('updatedAt', {
    header: 'Geändert',
    size: 82,
    cell: (info) => (
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--fs-xs)',
          color: 'var(--ink-2)',
        }}
      >
        {fmtDate(info.getValue())}
      </span>
    ),
    sortingFn: (a, b) =>
      new Date(a.original.updatedAt).getTime() -
      new Date(b.original.updatedAt).getTime(),
  }),
];

/**
 * "Verteiler" tab — a master/detail browser over the caller's contact lists.
 * The master is a search-driven table of Verteiler; selecting one opens its
 * detail page (`:listId`) in the resizable detail pane. A Verteiler is a named
 * distribution list whose members may span any number of business partners.
 */
export function VerteilerPage() {
  const { data: lists = [], isPending, isFetching } = useContactLists();
  const navigate = useNavigate();
  const { listId } = useParams<{ listId: string }>();
  const selectedId = listId ? Number(listId) : null;

  const [query, setQuery] = useState('');
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'name', desc: false },
  ]);
  const [creating, setCreating] = useState(false);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return lists.filter((list) => {
      if (kindFilter !== 'all' && list.kind !== kindFilter) return false;
      if (!term) return true;
      return (
        list.name.toLowerCase().includes(term) ||
        (list.description?.toLowerCase().includes(term) ?? false)
      );
    });
  }, [lists, query, kindFilter]);

  const table = useReactTable({
    data: filtered,
    columns: masterColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => String(row.id),
    enableSortingRemoval: false,
  });

  const handleRowClick = useCallback(
    (list: ContactList) => {
      navigate(`/i/business-partners/verteiler/${list.id}`);
    },
    [navigate],
  );

  return (
    <div className="vt-browser">
      <div className="bp-command-bar">
        <DsSearchInput
          className="bp-cmd-search"
          value={query}
          onSearch={setQuery}
          debounce={0}
          placeholder="Verteiler suchen…"
        />
        <DsSegmented
          options={KIND_FILTER_OPTIONS}
          value={kindFilter}
          onChange={setKindFilter}
          ariaLabel="Typ-Filter"
        />
        <DsButton
          variant="primary"
          size="small"
          onClick={() => setCreating(true)}
        >
          + Neuer Verteiler
        </DsButton>
        <span className="bp-cmd-count">
          <b>{filtered.length}</b> Verteiler
        </span>
      </div>

      <Group
        orientation="horizontal"
        className="bp-split"
        id="vt-split"
        defaultLayout={{ 'vt-master': 38, 'vt-detail': 62 }}
      >
        <Panel id="vt-master" minSize={24} className="bp-master-panel">
          {isPending ? (
            <div className="bp-empty">
              <div>
                <div className="big">Laden…</div>
              </div>
            </div>
          ) : (
            <DsTable
              table={table}
              fixedLayout
              selectedRowId={
                selectedId != null ? String(selectedId) : undefined
              }
              onRowClick={handleRowClick}
              emptyMessage={
                query ? 'Keine Treffer.' : 'Noch keine Verteiler.'
              }
            />
          )}
        </Panel>
        <Separator className="bp-resize-handle" />
        <Panel id="vt-detail" minSize={34} className="bp-detail-wrap">
          <Outlet
            context={
              {
                lists,
                listsPending: isPending,
                listsFetching: isFetching,
              } satisfies VerteilerListContext
            }
          />
        </Panel>
      </Group>

      {creating && (
        <ContactListModal
          list={null}
          onClose={() => setCreating(false)}
          onSaved={(created) =>
            navigate(`/i/business-partners/verteiler/${created.id}`)
          }
        />
      )}
    </div>
  );
}
